import {
  Autocomplete,
  AutocompleteProps,
  Box,
  CircularProgress,
  TextField,
  TextFieldProps,
} from "@mui/material";
import { useField } from "formik";
import React, { ReactNode } from "react";

import FieldErrorFeedbackFormatter from "components/forms/FieldErrorFeedbackFormatter";
import { MakeOptional } from "utils/types";

import FormikLabel from "../FormikLabel";

export type AutocompleteOptiontType<T = any> = { label: string; value: T };

interface FormikAutocompleteProps<
  Option extends AutocompleteOptiontType,
  DisableClearable extends boolean | undefined = undefined,
> extends MakeOptional<
  AutocompleteProps<Option, false, DisableClearable, false>,
  "renderInput"
> {
  name: string;
  label?: string | ReactNode;
  inputProps?: TextFieldProps;
  helperText?: TextFieldProps["helperText"];
}

/**
 * This Autocomplete does NOT accept:
 * - multiple choices
 * - free solo feature
 */
const FormikAutocomplete = <
  Option extends AutocompleteOptiontType,
  DisableClearable extends boolean | undefined = undefined,
>({
  name,
  options,
  label,
  loading,
  size,
  inputProps,
  helperText,
  ...rest
}: FormikAutocompleteProps<Option, DisableClearable>) => {
  const [field, meta, helpers] = useField<string | null>(name);

  const autocompleteValue =
    options.find((option) => option.value === field.value) ?? null;

  return (
    <Box>
      <FormikLabel>{label}</FormikLabel>
      <Autocomplete
        size={size}
        options={options}
        onChange={async (_e, value) => {
          helpers.setValue(value !== null ? value.value : null);
        }}
        // couldn't satisfy typescript, but 99.9% will work.
        // @ts-expect-error
        value={autocompleteValue}
        onBlur={field.onBlur}
        loading={loading}
        renderInput={(params) => (
          <TextField
            {...params}
            name={field.name}
            label=""
            error={meta.touched && !!meta.error}
            helperText={
              meta.touched && meta.error ? (
                <FieldErrorFeedbackFormatter error={meta.error} />
              ) : (
                helperText
              )
            }
            size={size}
            InputProps={{
              ...params.InputProps,

              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            {...inputProps}
          />
        )}
        {...rest}
      />
    </Box>
  );
};

export default FormikAutocomplete;
